import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { MessageService } from 'primeng/api';

/**
 * Interface for poll completion results
 */
export interface PollResult {
  /** The trace ID from the original request */
  traceId: string;
  /** The domain from the original request */
  domain: string;
  /** The complete response data from the polling endpoint */
  data: any;
  /** Indicates if the poll completed successfully or timed out */
  status: 'completed' | 'timeout' | 'not-found' | 'error';
  /** Optional message describing the result */
  message?: string;
}

/**
 * PollCompletionService - Broadcasts poll completion events across the application
 * 
 * This service allows any component or service to subscribe to poll completion events.
 * When a polling operation completes successfully, it broadcasts the result.
 */
@Injectable({
  providedIn: 'root'
})
export class PollCompletionService implements OnDestroy {
  private completionSubject = new Subject<PollResult>();

  public readonly completion$: Observable<PollResult> = this.completionSubject.asObservable();

  constructor(private messageService: MessageService) {}

  /**
   * Emits a poll completion event to all subscribers
   * @param result The poll result containing traceId, domain, and response data
   */
  emit(result: PollResult): void {
    this.completionSubject.next(result);
  }

  /**
   * Displays a user-friendly error message when polling encounters issues
   * This centralizes error message composition and display logic
   * 
   * @param result The poll result with status and error details
   * @param operationName A friendly name for the operation (e.g., "moving the document to the next phase")
   */
  displayPollError(result: PollResult, operationName: string): void {
    let severity: string;
    let summary: string;
    let detail: string;
    
    switch (result.status) {
      case 'timeout':
        severity = 'error';
        summary = 'Request Timeout';
        detail = `Your request for ${operationName} is taking longer than expected. ` +
                 `The page will display the latest data. If your changes are not reflected, ` +
                 `please contact support for assistance.`;
        break;
        
      case 'not-found':
        severity = 'error';
        summary = 'Request Not Found';
        detail = `Your request for ${operationName} could not be found. 
        It may have expired or been cancelled. Please retry, and if the error persists, contact support for assistance.`
        break;
        
      case 'error':
      default:
        severity = 'error';
        summary = 'Request Processing Issue';
        detail = `An issue occurred while processing your request for ${operationName}. 
        Please retry, and if the error persists, contact support for assistance.`
        break;
    }

    this.messageService.add({
      severity: severity,
      summary: summary,
      detail: detail,
      sticky: true
    });
  }

  ngOnDestroy(): void {
    this.completionSubject.complete();
  }
}
